import React from 'react';
import { Box, Container, Typography, Stack, Grid, Paper } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { PrimaryButton } from '../components/common/Button';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import GroupsIcon from '@mui/icons-material/Groups';
import CelebrationIcon from '@mui/icons-material/Celebration';
import SchoolIcon from '@mui/icons-material/School';
import Hero from '../components/Hero';
import heroImage from '../assets/hero/test.png';
import { events } from '../data/events';
import dayjs from 'dayjs';

const features = [
  {
    name: 'Learn and Innovate',
    description:
      'Gain access to hands-on workshops, coding challenges, and hackathons that help you develop practical skills and stay ahead in the tech world.',
    icon: LightbulbIcon,
  },
  {
    name: 'Be Part of a Thriving Community',
    description:
      'Join a diverse group of students and professionals passionate about technology, where collaboration and mentorship drive growth.',
    icon: GroupsIcon,
  },
  {
    name: 'Enjoy Fun Community Events',
    description:
      'Participate in game nights, movie screenings, and social gatherings that bring tech enthusiasts together for learning and laughter.',
    icon: CelebrationIcon,
  },
  {
    name: 'Build Your Future',
    description:
      'Network with industry leaders, explore internship opportunities, and work on projects that make a real-world impact.',
    icon: SchoolIcon,
  },
];

const Home = () => {
  // Get upcoming events for today and future
  const upcomingEvents = events
    .filter(event => event.date.isAfter(dayjs()) || event.date.isSame(dayjs(), 'day'))
    .slice(0, 3);

  return (
    <>
      <Hero
        title="Learn. Innovate. Connect. Have Fun."
        description="Join the ACM at ACC for more than just coding and tech workshops. From hackathons to game nights, we're a community where learning and laughter go hand in hand."
        primaryButtonText="Join Now"
        primaryButtonLink="/contact"
        imageSrc={heroImage}
      />
      {/* Features Section */}
      <Box
        sx={{
          position: 'relative',
          isolation: 'isolate',
          py: { xs: 8, md: 12 },
          bgcolor: 'background.default',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Stack 
            spacing={8} 
            alignItems="center"
            sx={{ position: 'relative', zIndex: 1 }}
          >
            {/* Header Section */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                maxWidth: '800px',
                width: '100%',
              }}
            >
              <Typography
                sx={{
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  color: theme => theme.palette.mode === 'dark' ? '#c1d82f' : 'primary.main',
                  mb: 2,
                }}
              >
                Why Join ACM ACC?
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  color: 'text.primary',
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Building the Future of Technology Together
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '1.125rem', md: '1.25rem' },
                  color: 'text.secondary',
                  lineHeight: 1.8,
                  textAlign: 'center',
                  maxWidth: '600px',
                }}
              >
                Join a vibrant community with access to resources, events, and mentorship that empower you to achieve your tech goals.
              </Typography>
            </Box>

            <Grid container spacing={4}>
              {features.map((feature) => (
                <Grid item xs={12} md={6} key={feature.name}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      height: '100%',
                      position: 'relative',
                      borderRadius: 2,
                      textAlign: 'center',
                      transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: theme => `0 8px 24px ${theme.palette.purple.main}20`,
                      },
                    }}
                  >
                    <Stack 
                      spacing={2}
                      alignItems="center"
                    >
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          bgcolor: 'primary.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          mb: 1,
                        }}
                      >
                        <feature.icon sx={{ fontSize: 24 }} />
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: 'text.primary',
                        }}
                      >
                        {feature.name}
                      </Typography>
                      <Typography
                        sx={{
                          color: 'text.secondary',
                          lineHeight: 1.7,
                        }}
                      >
                        {feature.description}
                      </Typography>
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Stack>
        </Container>
      </Box>

      {/* Events Section */}
      <Box
        sx={{
          position: 'relative',
          isolation: 'isolate',
          py: { xs: 8, md: 12 },
          bgcolor: 'background.paper',
        }}
      >
        <Container maxWidth="lg">
          <Stack spacing={8}>
            {/* Header */}
            <Stack spacing={2}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  color: 'text.primary',
                }}
              >
                Upcoming Events
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '1.125rem', md: '1.25rem' },
                  color: 'text.secondary',
                }}
              >
                Join us for workshops, social events, and learning opportunities.
              </Typography>
            </Stack>

            {/* Events Grid */}
            <Grid container spacing={4}>
              {upcomingEvents.map((event) => (
                <Grid item xs={12} md={4} key={event.id}>
                  <Paper
                    elevation={0}
                    sx={{
                      height: '100%',
                      p: 3,
                      borderRadius: 2,
                      transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: theme => `0 8px 24px ${theme.palette.purple.main}20`,
                      },
                    }}
                  >
                    <Stack spacing={2}>
                      {/* Date and Category */}
                      <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                      >
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            color: 'text.secondary',
                          }}
                        >
                          {event.time}
                        </Typography>
                        <Typography
                          sx={{
                            padding: '4px 8px',
                            borderRadius: '100px',
                            backgroundColor: 'primary.main',
                            color: 'white',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                          }}
                        >
                          {event.type.charAt(0).toUpperCase() + event.type.slice(1)}
                        </Typography>
                      </Stack>

                      {/* Title and Description */}
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                        }}
                      >
                        {event.title}
                      </Typography>
                      <Typography
                        sx={{
                          color: 'text.secondary',
                          fontSize: '0.875rem',
                          lineHeight: 1.6,
                        }}
                      >
                        {event.description}
                      </Typography>

                      {/* Location */}
                      <Typography
                        sx={{
                          color: 'text.secondary',
                          fontSize: '0.875rem',
                          mt: 'auto',
                          pt: 2,
                        }}
                      >
                        {event.location}
                      </Typography>
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* Add a "View All Events" button */}
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <PrimaryButton
                component={RouterLink}
                to="/events"
                sx={{ mt: 4 }}
              >
                View All Events
              </PrimaryButton>
            </Box>
          </Stack>
        </Container>
      </Box>

      {/* Temporarily commented out RiverHacks Section */}
      {/*
      <Box 
        sx={{ 
          position: 'relative',
          isolation: 'isolate',
          py: { xs: 8, md: 12 },
          bgcolor: 'background.default',
          overflow: 'hidden',
        }}
      >
        <HeroPattern />
        
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(180deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
            zIndex: -1,
          }}
        />

        <Container maxWidth="lg">
          ... Rest of RiverHacks content ...
        </Container>
      </Box>
      */}
    </>
  );
};

export default Home;
